import { POST_LOAD_GRACE_MS } from '@client/hooks/system/usePageSettled';
import type { Pathname } from '@shared/navigation/routes.gen';
import type { AnyRouter } from '@tanstack/react-router';

interface ScheduleOnIdleOptions {
    /** Skip the post-load grace only during a tracked client-side navigation. */
    postLoadGrace?: 'always' | 'cold-load-only';
    /** Maximum time requestIdleCallback may wait for an idle frame. */
    timeoutMs?: number;
}

let hasRenderedInitialDocument = false;
let isInitialDocumentNavigation = true;

/**
 * Routes that only hand the initial document on to its landing page. The Auth0
 * callback exchanges the code on a freshly loaded document, then navigates in
 * app to the requested page, whose lazy chunks are still cold.
 */
const INITIAL_DOCUMENT_HANDOFF_PATHS: ReadonlySet<string> = new Set<Pathname>(['/callback']);

/**
 * Feed router lifecycle events into idle scheduling so a late initial mount is
 * not mistaken for an in-app navigation merely because `window.load` fired.
 * The initial-document state survives its first render, other than a hand-off
 * route's, and flips only when a later router load begins.
 */
export function trackIdleSchedulingNavigation(router: AnyRouter): () => void {
    const unsubscribeBeforeLoad = router.subscribe('onBeforeLoad', () => {
        if (hasRenderedInitialDocument) {
            isInitialDocumentNavigation = false;
        }
    });
    const unsubscribeRendered = router.subscribe('onRendered', ({ toLocation }) => {
        if (isInitialDocumentNavigation && !INITIAL_DOCUMENT_HANDOFF_PATHS.has(toLocation.pathname)) {
            hasRenderedInitialDocument = true;
        }
    });

    return () => {
        unsubscribeBeforeLoad();
        unsubscribeRendered();
    };
}

/**
 * Whether a client-side navigation has begun since the initial document first
 * rendered its landing page. Stays `false` for a late initial-document mount,
 * including a post-login landing reached from the auth callback, and when no
 * router is tracked, so callers fall back to their cold-load behaviour.
 */
export function hasClientNavigationStarted(): boolean {
    return !isInitialDocumentNavigation;
}

/** Test-only reset of the module-level document-navigation signal. */
export function __resetIdleSchedulingNavigationForTests(): void {
    hasRenderedInitialDocument = false;
    isInitialDocumentNavigation = true;
}

/**
 * Wait for `window.load`, optionally wait {@link POST_LOAD_GRACE_MS}, then run
 * `cb` on the next idle frame where the browser supports it. The default keeps
 * the grace on already-loaded documents for existing background-work callers;
 * `postLoadGrace: 'cold-load-only'` lets tracked SPA navigation schedule
 * immediately while retaining the grace for late initial-document mounts.
 * Browsers without `requestIdleCallback` (Safari ≤ 16) run the callback after
 * any applicable post-load grace instead.
 *
 * Use for non-critical work that should not compete for bandwidth or
 * main-thread time with the initial render — telemetry mutations
 * (`refreshLoginTimestamp`), version polling first-fire
 * (`getAppBuildVersion`), session-recording start
 * (`posthog.startSessionRecording`), and similar background tasks.
 *
 * The callback is fire-and-forget; this helper does not return a handle
 * and does not surface errors. Callers must `.catch()` their own
 * promises if they need to log failures.
 */
export function scheduleOnIdle(cb: () => void, options: ScheduleOnIdleOptions = {}): void {
    const { postLoadGrace = 'always', timeoutMs } = options;

    const scheduleIdle = (withPostLoadGrace: boolean) => {
        const requestIdle = () => {
            if (typeof window.requestIdleCallback === 'function') {
                window.requestIdleCallback(() => cb(), timeoutMs === undefined ? undefined : { timeout: timeoutMs });
                return;
            }

            cb();
        };

        if (withPostLoadGrace) {
            window.setTimeout(requestIdle, POST_LOAD_GRACE_MS);
            return;
        }

        requestIdle();
    };

    if (document.readyState === 'complete') {
        scheduleIdle(postLoadGrace === 'always' || isInitialDocumentNavigation);
        return;
    }

    window.addEventListener('load', () => scheduleIdle(true), { once: true });
}
